/**
 * Name des HANDELNDEN Nutzers — wer hat verbucht, versendet, geprüft.
 *
 * (Nicht zu verwechseln mit services/personName.ts: das zerlegt Kundennamen in
 * Anrede/Titel/Vor-/Nachname für DATEV und Briefanrede. Hier geht es um den
 * Mitarbeiternamen, der in Buchungen, Archiv und Kundendokumenten steht.)
 *
 * Auftrag des Betreibers: „Da muss überall immer der richtige Name stehen."
 * Erfüllt war das bisher nur, wo jemand daran gedacht hat — an mehreren Stellen
 * entstand statt eines Namens ein Trümmerstück, das anschließend jeden echten
 * Namen verdrängte, weil es truthy ist und damit jede `||`-Kette gewinnt:
 *
 *   `${first_name} ${last_name}`  → "Max undefined" bei fehlendem Nachnamen,
 *                                   " " bei leerem Profil (truthy!)
 *   'System'                      → Platzhalter, wenn beim Versand kein Profil
 *                                   geladen war (EmailModal.bookingOpts)
 *   'coordinator'                 → Rollen-Literal aus Projektanlage/CSV-Import
 *
 * Im Leistungsnachweis FÜR DEN KUNDEN stand dann „System" oder „Max undefined"
 * in der Koordinatorenspalte — obwohl das Archiv den richtigen Prüfer kennt.
 *
 * Deshalb: kein Name ist besser als ein kaputter. Was hier durchfällt, gibt ''
 * zurück und lässt damit den nächsten Fallback (Archivname) greifen. Dieselbe
 * Bewertung nimmt cloudService.ts beim Archivieren des Berichts vor.
 */

interface ActorNameParts {
    first_name?: string | null;
    last_name?: string | null;
}

/**
 * Rollenwörter, die nie eine Person benennen. Kleingeschrieben verglichen,
 * weil sie mal als 'System', mal als 'system' geschrieben wurden.
 */
const PLACEHOLDER_NAMES = new Set(['system', 'system-admin', 'coordinator']);

/**
 * Derselbe Platzhalter mit einem Zusatz in Klammern: 'System (Linked)' schreibt
 * die Buchung einer Tochter auf die Mutter, 'System (Legacy)' die Nachbuchung
 * aus der Buchhaltungsansicht, 'System (Sammelnachtrag)' der Sammelnachtrag.
 * Ohne diese Regel fielen sie NICHT durch — sie sind truthy, gewinnen damit
 * jede ||-Kette gegen den Archivnamen und standen wörtlich in der
 * Koordinatorenspalte des Leistungsnachweises (Projekt 6172).
 *
 * Bewusst nur die abschliessende Klammer und nur, wenn davor der reine
 * Platzhalter steht: „Anna Admin (Vertretung)" ist ein Mensch und bleibt.
 * Damit faellt auch jeder KUENFTIGE 'System (…)'-Stub durch, ohne dass ihn
 * hier jemand nachtragen muss.
 */
const stripSuffix = (name: string): string => name.replace(/\s*\([^)]*\)$/, '').trim();

/** Randleerzeichen weg, mehrfache Leerzeichen im Inneren auf eines zusammen. */
const collapse = (value?: string | null): string => (value || '').replace(/\s+/g, ' ').trim();

/**
 * Ist das ein verwertbarer Name? Sonst '' — bewusst ein leerer String und kein
 * `undefined`, damit die vorhandenen `||`-Ketten unverändert weiterlaufen.
 *
 * „undefined"/„null" werden als ganzes Wort geprüft und nicht als Teilstring:
 * „Nullmeyer" und „Undine" sind echte Namen und dürfen nicht mitgerissen werden.
 */
export const realActorName = (value?: string | null): string => {
    const name = collapse(value);
    if (!name) return '';
    const lower = name.toLowerCase();
    if (PLACEHOLDER_NAMES.has(lower)) return '';
    if (PLACEHOLDER_NAMES.has(stripSuffix(lower))) return '';
    if (lower.split(' ').some(part => part === 'undefined' || part === 'null')) return '';
    return name;
};

/**
 * Vor- und Nachname zu einem Namen zusammensetzen — nie ein Trümmerstück.
 *
 * Fehlt eine Hälfte, bleibt die andere allein stehen; fehlen beide, kommt ''
 * heraus statt eines Leerzeichens. Genau das war der Unterschied zwischen
 * „kein Name, nimm den Archivnamen" und „ein Name, der den Archivnamen
 * verdrängt".
 */
export const buildActorName = (person: ActorNameParts | null | undefined): string =>
    realActorName(`${collapse(person?.first_name)} ${collapse(person?.last_name)}`);
